<script lang="ts" setup>
import {computed} from 'vue';
import InternalChatUser from '@src/types/InternalChatUser';
import {resolveUrl} from '@src/helpers/utils';
import {getLastMessageDate} from '@src/helpers/lastMessageHelper';
import useStore from '@src/stores/store';
import {DevicePhoneMobileIcon} from "@heroicons/vue/20/solid"


const props = defineProps<{
    user: InternalChatUser;
    isSelected: boolean;
    unreadCount: number;
}>();

const store = useStore();

const lastMessage = computed(() => props.user.messages[0] ?? null);

const lastMessagePreview = computed(() => {
    if (!lastMessage.value) return '';
    const prefix = lastMessage.value.sender_id === store.user?.id ? 'Você: ' : '';
    if (lastMessage.value.attachment) return `${prefix}📎 ${lastMessage.value.attachment.original_name}`;
    return `${prefix}${lastMessage.value.body ?? ''}`;
});

const lastMessageTime = computed(() => {
    if (!lastMessage.value?.created_at) return '';
    return getLastMessageDate(lastMessage.value.created_at);
});

const initials = computed(() =>
    props.user.name
        .split(' ')
        .slice(0, 2)
        .map(w => w[0]?.toUpperCase() ?? '')
        .join('')
);

const customClass = computed(() => {
    let isOnline = props.user.is_online;
    let isAppLog = props.user.device !== 'Navegador'
    let baseClass = 'absolute bottom-0 right-0 flex items-center'
    if (isOnline && isAppLog) {
        baseClass += ` w-[18px] h-6 bg-white dark:bg-gray-800 rounded-md`
    } else {
        baseClass += ` w-5 h-5 ring-2 ring-white dark:ring-gray-800 rounded-full`
        if (isOnline) {
            baseClass += ` bg-green-400`
        } else {
            baseClass += ` bg-gray-300 dark:bg-gray-400`
        }
    }
    return baseClass;
})
</script>

<template>
    <div
        :class="[
            store.settings.darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
            isSelected ? 'bg-gray-100 dark:bg-gray-600' : ''
        ]"
        class="flex items-center px-4 py-3 cursor-pointer md:rounded-sm transition-colors w-full relative border-b-gray-200 border-b-1 md:border-b-0 last:border-b-0 "
    >
        <!-- avatar -->
        <div class="relative flex-shrink-0">
            <div
                v-if="user.profile_photo_url"
                :style="{ backgroundImage: `url(${resolveUrl(user.profile_photo_url)})` }"
                class="w-8 h-8 rounded-full bg-cover bg-center bg-gray-200"
            />
            <div
                v-else
                class="w-8 h-8 rounded-full bg-indigo-200 dark:bg-indigo-700 flex items-center justify-center text-xs font-semibold text-indigo-700 dark:text-indigo-200"
            >
                {{ initials }}
            </div>

            <!-- status badge -->
            <span
                :aria-label="user.is_online ? 'Online' : 'Offline'"
                :class="customClass"
                :title="user.is_online ? 'Online' : 'Offline'">
                <DevicePhoneMobileIcon v-if="user.device !== 'Navegador'" class="text-green-400 h-6 w-6"/>
            </span>
        </div>

        <!-- info -->
        <div class="flex-1 min-w-0 ml-3">
            <div class="flex items-center justify-between">
                <span class="text-sm font-semibold text-slate-800 dark:text-white truncate">
                    {{ user.name }}
                </span>
                <span v-if="lastMessageTime" class="text-xs text-gray-400 dark:text-gray-400 flex-shrink-0 ml-2">
                    {{ lastMessageTime }}
                </span>
            </div>
            <div class="flex items-center justify-between mt-0.5">
                <span class="text-xs text-gray-500 dark:text-gray-400 truncate pr-2">
                    {{ lastMessagePreview }}
                </span>
                <span
                    v-if="unreadCount > 0"
                    class="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-brand-300 dark:bg-brand-400 text-white text-[10px] font-semibold"
                >
                    {{ unreadCount > 99 ? '99+' : unreadCount }}
                </span>
            </div>
        </div>
    </div>
</template>
